import { icsFileName, validateEvent, type EventDraft } from '@project/event-format';
import {
  eventRecap,
  mergeMessages,
  problemMessage,
  type EventCreatorMessages,
} from '@project/event-creator-web';
import { type IntakeBatch, type IntakeFlag, type IntakeForm } from '@project/core';

import { fillSite, type SiteMessages } from './i18n';

/**
 * A flag in the reader's language.
 *
 * The domain stamps every flag with both a `kind` and an English `note`. The note is the one
 * `@project/core` composed for the native app; here it is the `kind` that is read and the note
 * that is dropped, so a German reader gets a German flag rather than the English sentence a
 * package they never see happened to build.
 */
function flagText(flag: IntakeFlag, m: SiteMessages): string {
  if (flag.kind === 'low-confidence') return m.flagLowConfidence;
  const template =
    flag.duplicateOf.scope === 'batch' ? m.flagDuplicateBatch : m.flagDuplicateCalendar;
  return fillSite(template, { title: flag.duplicateOf.title });
}

/**
 * The events collected so far, however they got here — filled in on the form tab, or read out of
 * pasted text, a document or a photographed flyer on the other one.
 *
 * It sits under both tabs rather than inside either, because it belongs to neither: the whole
 * point of collecting is that a season pasted in one tab and a line typed in the other come out
 * as one file. Every row can be taken back out, and every row can be opened in the form — which
 * is the correction surface for anything the reader got wrong.
 */
export function EventList({
  batch,
  editing,
  hasUnsavedChanges,
  onSave,
  m,
  creatorMessages,
  locale,
  onNew,
  onEdit,
  onRemove,
  onDownload,
}: {
  readonly batch: IntakeBatch;
  /** Which row the form tab is currently editing, so it can be marked as such. */
  readonly editing: string | null;
  readonly hasUnsavedChanges: boolean;
  readonly onSave: () => void;
  readonly m: SiteMessages;
  readonly creatorMessages: Partial<EventCreatorMessages>;
  readonly locale: string;
  readonly onNew: () => void;
  readonly onEdit: (form: IntakeForm) => void;
  readonly onRemove: (formId: string) => void;
  readonly onDownload: (drafts: readonly EventDraft[], fileName: string) => void;
}): React.JSX.Element {
  const messages = mergeMessages(creatorMessages);
  const valid = batch.forms.filter((form) => validateEvent(form.draft).length === 0);
  const invalidCount = batch.forms.length - valid.length;
  const assumptions = {
    'all-day': m.workflow.allDay,
    'day-month': m.workflow.dayMonth,
    'inherited-date': m.workflow.inheritedDate,
    'missing-title': m.workflow.missingTitle,
  };
  return (
    <section className="collected" aria-labelledby="collected-heading">
      <div className="collected-header">
        <h2 id="collected-heading" className="collected-headline">
          {fillSite(m.workflow.headline, { count: batch.forms.length })}
        </h2>
        <button type="button" className="linky" onClick={onNew}>
          {m.editingStartNew}
        </button>
      </div>
      {hasUnsavedChanges && (
        <div className="collected-unsaved" role="status">
          <p>{m.unsavedChanges}</p>
          <button type="button" className="funnel-go" onClick={onSave}>
            {m.saveChanges}
          </button>
        </div>
      )}
      <div className="collected-downloads">
        {invalidCount > 0 && (
          <p role="status">{fillSite(m.workflow.blocked, { count: invalidCount })}</p>
        )}
        <button
          type="button"
          className="funnel-go"
          disabled={invalidCount > 0 || hasUnsavedChanges}
          onClick={() =>
            onDownload(
              batch.forms.map((form) => form.draft),
              'events.ics',
            )
          }
        >
          {batch.forms.length === 1
            ? m.listDownloadOnlyOne
            : fillSite(m.listDownloadAll, { count: batch.forms.length })}
        </button>
        {invalidCount > 0 && valid.length > 0 && (
          <button
            type="button"
            className="funnel-go"
            disabled={hasUnsavedChanges}
            onClick={() =>
              onDownload(
                valid.map((form) => form.draft),
                'events.ics',
              )
            }
          >
            {fillSite(m.workflow.valid, { count: valid.length })}
          </button>
        )}
      </div>
      <ul className="collected-list">
        {batch.forms.map((form) => (
          <li
            key={form.id}
            id={`event-${form.id}`}
            tabIndex={-1}
            className={`funnel-form${form.id === editing ? ' collected-editing' : ''}`}
          >
            <strong>{form.draft.title || m.listUntitled}</strong>
            <span className="funnel-when">{eventRecap(form.draft, messages, locale, false)}</span>
            {form.flags.map((flag, index) => (
              <span key={index} className="funnel-flag">
                {flagText(flag, m)}
              </span>
            ))}
            {(form.assumptionCodes?.map((code) => assumptions[code]) ?? form.assumptions).map(
              (assumption, index) => (
                <span key={index} className="funnel-assumption">
                  {assumption}
                </span>
              ),
            )}
            {/* Shown, never used to drop the event: a mostly-right event corrected in two fields
                beats one refused at the door and retyped from scratch. */}
            {form.problems.map((issue, index) => (
              <span key={index} className="funnel-flag">
                {problemMessage(issue, messages)}
              </span>
            ))}
            <span className="funnel-row">
              <button type="button" onClick={() => onEdit(form)}>
                {form.id === editing ? m.unsavedDialog.keep : m.listEdit}
              </button>
              <button
                type="button"
                disabled={form.problems.length > 0 || (hasUnsavedChanges && form.id === editing)}
                onClick={() => onDownload([form.draft], icsFileName(form.draft))}
              >
                {m.listDownloadOne}
              </button>
              <button
                type="button"
                className="collected-remove"
                disabled={form.id === editing}
                onClick={() => onRemove(form.id)}
                aria-label={fillSite(m.listRemoveLabel, {
                  title: form.draft.title || m.listUntitled,
                })}
              >
                {m.listRemove}
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
